// Today hero — the mobile-only first screenful (Ship 1, Aug 25, 2026).
//
// Greg's rule: "I don't want to have to swipe down to look at what I can do
// that day." On phones this card leads the page: today's schedule, the fastest
// jumps into work, and today's live spiff nudge. Desktop never renders it —
// the day-detail column already carries today there. The parent gates the
// mount on the widget registry (sales roles only), wraps it in `lg:hidden`,
// and passes today's already-fetched schedule items (no extra query here).
//
// Spiff thresholds come from SPIFF_PROGRAM.target — never re-derived here.

import { Link } from "react-router-dom";
import {
  SPIFF_PROGRAM,
  todayDowET,
  windowClosed,
  type SpiffStat,
} from "../../../lib/weeklySpiffs";
import { logCardClick } from "../../../lib/uiEvents";

export interface TodayHeroItem {
  t: "meet" | "reinsp" | "build";
  ho: string;
  addr: string;
  time: string;
  dealId: string;
}

const fmtHalf = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(1));
const DOW_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** First open, unearned spiff for this rep → one actionable sentence. */
function spiffNudge(stat: SpiffStat): string | null {
  const dow = todayDowET();
  for (const p of SPIFF_PROGRAM) {
    if (stat.earned[p.key]) continue;
    if (windowClosed(p.key, dow)) continue;
    const prize = `$${p.prizeCents / 100}`;
    // The window's honest deadline: "tonight" only on the cutoff day itself.
    const due = dow === p.cutoffDow ? "tonight" : `by ${DOW_SHORT[p.cutoffDow]} EOD`;
    const counter =
      p.key === "monday" ? stat.claimsMon
      : p.key === "tuesday" ? stat.apptsTue
      : p.key === "hump" ? stat.claimsWed
      : stat.perfectTotal;
    const need = Math.max(0, p.target - counter);
    const unit =
      p.key === "tuesday" ? `appt${need === 1 ? "" : "s"}`
      : p.key === "perfect" ? "claims or signed IAs"
      : `claim${need === 1 ? "" : "s"}`;
    const progress = counter > 0 ? `${fmtHalf(counter)}/${fmtHalf(p.target)} — ` : "";
    return `${progress}${fmtHalf(need)} more ${unit} ${due} = ${p.name} ${prize}`;
  }
  return null;
}

export default function TodayHero({
  items,
  loading,
  myStat,
  showWorkLinks,
}: {
  items: TodayHeroItem[];
  /** Week schedule still fetching — show a skeleton, never a false "nothing". */
  loading: boolean;
  myStat: SpiffStat | null;
  /** Field roles get the going-cold / storm-pool jump links. */
  showWorkLinks: boolean;
}) {
  const nudge = myStat ? spiffNudge(myStat) : null;
  const dateLabel = new Date().toLocaleDateString("en-US", {
    weekday: "long",
    month: "short",
    day: "numeric",
  });

  return (
    <div className="rounded-2xl border-2 border-bardin-orange bg-white p-3.5 mb-4">
      <div className="text-[10px] font-extrabold uppercase tracking-widest text-bardin-orange">
        {dateLabel}
      </div>

      <div className="mt-1.5">
        {loading ? (
          <div className="space-y-1.5 py-0.5">
            <div className="h-5 rounded bg-slate-100 animate-pulse" />
            <div className="h-5 rounded bg-slate-100 animate-pulse w-3/4" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-xs text-slate-400 py-1">Nothing on your schedule today.</div>
        ) : (
          items.map((it, i) => (
            <Link
              key={`${it.dealId}-${it.t}-${i}`}
              to={`/deal/${it.dealId}`}
              onClick={() => logCardClick("widget_today_row")}
              className="flex items-center gap-2 py-1.5"
            >
              <span className="text-sm">{it.t === "build" ? "🔨" : "🏠"}</span>
              <span className="text-[13px] font-bold text-bardin-ink truncate">{it.ho}</span>
              <span className="text-[11px] text-slate-400 truncate flex-1">
                {it.t === "build" ? "build day" : it.t === "reinsp" ? "2nd adjuster" : it.addr}
              </span>
              {it.time && (
                <span className="px-1.5 py-0.5 rounded bg-teal-50 text-teal-700 text-[10px] font-extrabold">
                  {it.time}
                </span>
              )}
            </Link>
          ))
        )}
      </div>

      {showWorkLinks && (
        <div className="flex gap-4 mt-2 text-[11.5px] font-bold">
          <Link
            to="/run-the-play"
            onClick={() => logCardClick("widget_today_cold")}
            className="text-bardin-orange"
          >
            Run the Play ›
          </Link>
          <Link
            to="/call-list"
            onClick={() => logCardClick("widget_today_pool")}
            className="text-bardin-orange"
          >
            Storm pool ›
          </Link>
        </div>
      )}

      {nudge && (
        <div className="mt-2.5 rounded-lg bg-amber-50 text-amber-800 px-2.5 py-1.5 text-[11.5px] font-semibold">
          ⚡ {nudge}
        </div>
      )}
    </div>
  );
}
